import { Box, Grid, Paper, Tab, Tabs, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useAuth } from "../../contexts/auth.context";
import Breadcrumb from "../../core/components/Breadcrumb";
import { VList } from "../../core/components/VList";
import { VListItem, VListItemType } from "../../core/components/VListItem";
import { useCurriculum } from "../../hooks/useCurriculum";
import { useMobile } from "../../hooks/useMobile";
import { useOrganisation } from "../../hooks/useOrganisation";
import Spinner from "../layout/Spinner";
import { TabPanel } from "../programme/screens/ViewProgramme";
import { useHomeStyles } from "./Home.style";
import { HomeViewItem } from "./HomeViewItem";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { Alert } from "@mui/material";
import { ComingSoonCard } from "./components/ComingSoonCard";
import { StoryCollectionCard } from "./components/StoryCollectionCard";
import { CourseDemoLockOverlay } from "./components/CourseDemoLockOverlay";
import { useAppNavigate } from "../../hooks/useAppNavigate";
import { COURSE_DEMO_UNLOCK_MESSAGE, isCollectionEntitledForDemo } from "../../utils/courseDemo";

export const ViewCurriculum = (): JSX.Element => {
    const params = useParams();
    const id = params.id!;
    const { curriculumInfo, getCurriculum } = useCurriculum();
    const { isTablet, isMobile } = useMobile();
    const { user } = useAuth();
    const { organisation, getOrganisation } = useOrganisation();
    const demoOrganisation = organisation ?? user?.Organisation;

    const { push } = useAppNavigate();

    const numberMissingCollections = curriculumInfo?.StoryCollection?.length
        ? 4 - curriculumInfo?.StoryCollection.length
        : -1;

    const [tabValue, setTabValue] = useState<number>(0);

    useEffect(() => {
        if (id && !isNaN(parseInt(id))) {
            getCurriculum(parseInt(id));
        }
    }, [id, getCurriculum]);

    useEffect(() => {
        if (user?.id) {
            getOrganisation(user.id);
        }
    }, [user, getOrganisation]);

    const classes = useHomeStyles();

    return (
        <Box display={"flex"} width={"100%"} style={{ overflow: "hidden" }}>
            {curriculumInfo ? (
                <>
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            {!isTablet && isMobile && <div className={classes.toolbar} />}
                            <Box pl={!isTablet && isMobile ? 2 : 4} pt={!isTablet && isMobile ? 6 : 2}>
                                <Breadcrumb
                                    crumbs={[
                                        {
                                            name: `${"Explore"}`,
                                            url: `${"/explore"}`,
                                        },
                                    ]}
                                    current={""}
                                />
                            </Box>
                            <div id="curriculum-details" style={{ height: "95%" }}>
                                <HomeViewItem
                                    title={`${curriculumInfo?.name}`}
                                    description={curriculumInfo?.description}
                                    colour={curriculumInfo?.colour}
                                />
                            </div>
                        </Grid>
                        {/* Desktop view - visible on md and up */}
                        <Grid item xs={12} sx={{ display: { xs: "none", md: "block" } }}>
                            <Box display={"flex"} p={4} pt={0} id="curriculum-collections">
                                <Grid container spacing={3} alignItems="stretch">
                                    <Grid item xs={12}>
                                        {demoOrganisation && demoOrganisation.Tier?.tier === "Demo" && (
                                            <div className={classes.toolbar} />
                                        )}
                                        <Typography variant="h4">Collections in this curriculum</Typography>
                                    </Grid>
                                    {curriculumInfo.StoryCollection && curriculumInfo.StoryCollection.length > 0
                                        ? curriculumInfo.StoryCollection?.sort((a, b) => (a.name < b.name ? 1 : 0)).map(
                                              (item, index) => (
                                                  <Grid item sm={3} key={item.id} style={{ display: "flex" }}>
                                                      <StoryCollectionCard
                                                          item={item}
                                                          curriculumId={curriculumInfo.id}
                                                          index={index}
                                                          curriculumColour={curriculumInfo.colour}
                                                          locked={
                                                              !isCollectionEntitledForDemo(item.id, demoOrganisation)
                                                          }
                                                      />
                                                  </Grid>
                                              ),
                                          )
                                        : Array.from({ length: 4 }).map((_c, index) => (
                                              <Grid item xs={3} key={index} style={{ display: "flex" }}>
                                                  <ComingSoonCard />
                                              </Grid>
                                          ))}
                                    {numberMissingCollections > 0
                                        ? Array.from({ length: numberMissingCollections }).map((_c, index) => (
                                              <Grid item xs={3} key={index} style={{ display: "flex" }}>
                                                  <ComingSoonCard />
                                              </Grid>
                                          ))
                                        : null}
                                </Grid>
                            </Box>
                        </Grid>

                        {/* Mobile/Tablet view - visible on xs and sm only */}
                        <Grid item xs={12} sx={{ display: { xs: "block", md: "none" } }}>
                            <Box bgcolor="secondaryShade.main" width={1} px={2}>
                                <Tabs
                                    textColor="primary"
                                    value={tabValue}
                                    onChange={(_event, newValue) => setTabValue(newValue)}
                                >
                                    <Tab label={"Collections"} />
                                </Tabs>
                                <TabPanel value={tabValue} index={0}>
                                    <VList>
                                        {curriculumInfo.StoryCollection && curriculumInfo.StoryCollection.length > 0 ? (
                                            curriculumInfo.StoryCollection?.map((item) => {
                                                const locked = !isCollectionEntitledForDemo(item.id, demoOrganisation);
                                                return (
                                                    <Box mb={1} key={item.id}>
                                                        <Paper
                                                            elevation={0}
                                                            aria-disabled={locked || undefined}
                                                            sx={
                                                                locked
                                                                    ? {
                                                                          position: "relative",
                                                                          overflow: "hidden",
                                                                          cursor: "not-allowed",
                                                                          "&:hover .course-demo-lock-overlay": {
                                                                              opacity: 0.92,
                                                                          },
                                                                      }
                                                                    : undefined
                                                            }
                                                        >
                                                            {locked && <CourseDemoLockOverlay hideMessage />}
                                                            <VListItem
                                                                type={VListItemType.Primary}
                                                                primaryText={`${item.name} ${item.ageRange}${
                                                                    locked ? ` — ${COURSE_DEMO_UNLOCK_MESSAGE}` : ""
                                                                }`}
                                                                textProps={{ color: "primary" }}
                                                                onClick={() => {
                                                                    if (locked) {
                                                                        return;
                                                                    }
                                                                    push(
                                                                        `/explore/curriculum/${curriculumInfo.id}/collection/${item.id}`,
                                                                    );
                                                                }}
                                                            >
                                                                {!locked && <ArrowForwardIosIcon color="primary" />}
                                                            </VListItem>
                                                        </Paper>
                                                    </Box>
                                                );
                                            })
                                        ) : (
                                            <Alert severity="info">Coming Soon....</Alert>
                                        )}
                                    </VList>
                                </TabPanel>
                            </Box>
                        </Grid>
                    </Grid>{" "}
                </>
            ) : (
                <Box m="auto" justifyContent="center">
                    <Spinner />
                </Box>
            )}
        </Box>
    );
};
